import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { getrefest, getrefid } from '../../../redux/actions';
import Card from "../Inbound/Card";
import HashLoader from 'react-spinners/HashLoader';
import styles from "../Inbound/inbound.module.css";
import { useNavigate } from "react-router-dom";
import {
    FaSearch,
    FaFilter,
    FaTable,
    FaCalendarAlt,
    FaBuilding,
    FaMapMarkerAlt,
    FaIndustry,
    FaWeightHanging,
    FaBox,
    FaCheckCircle,
    FaArrowRight,
    FaFileAlt
} from "react-icons/fa";

const Inbound = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const EstadoRef = useSelector((state) => state.estadoref);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [estadoFiltro, setEstadoFiltro] = useState(null);

    useEffect(() => {
        const loadRef = async () => {
            await dispatch(getrefest());
            setLoading(false);
        };

        loadRef();
    }, [dispatch]);

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(getrefest());
        }, 8000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const handleRowClick = (refData) => {
        dispatch(getrefid(refData.Idref));

        navigate(
            refData.paralelo === 1
                ? `/detallesinboundparalelo/${refData.Idref}`
                : `/detallesinbound/${refData.Idref}`,
            {
                state: {
                    referencia: refData.ref,
                    fecha: refData.fecha_mayor,
                    estado: refData.descripcion_estado,
                    cliente: refData.nombre_cliente,
                    Idref: refData.Idref,
                    Lugar: refData.nombre_lugar,
                    Ubicacion: refData.nombre_ubicacion,
                    Material: refData.nombre_material,
                    paralelo: refData.paralelo,
                    registros: refData.Registros ?? null
                }
            }
        );
    };

    const filteredRecords = EstadoRef.Registros
        ? EstadoRef.Registros.filter(refData => {
            const estadoOriginal = refData.descripcion_estado;

            if (!estadoFiltro && estadoOriginal === "Finalizado") {
                return false;
            }

            if (estadoFiltro === "Finalizado") {
                return estadoOriginal === "Finalizado";
            }

            const matchesSearch =
                refData.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
                refData.nombre_cliente.toLowerCase().includes(searchTerm.toLowerCase());

            const estadoNormalizado =
                estadoOriginal === "Lote cerrado" ? "En curso" :
                    estadoOriginal === "Creado" ? "Sin iniciar" :
                        estadoOriginal;

            const matchesEstado =
                estadoFiltro ? estadoNormalizado === estadoFiltro : true;

            return matchesSearch && matchesEstado;
        })
        : [];

    const formatDate = (dateString) => {
        if (!dateString) return "---";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "---";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${d} ${hh}:${mm}`;
    };

    const formatAcomulado = (value) => {
        if (value == null) return "0,00";
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).replace(/\./g, ' ');
    };

    return (
        <div className={styles.mainContainer}>
            <h2 className={styles.title}>Control de Referencias Inbound</h2>

            <div className={styles.controlPanel}>
                <div className={styles.searchContainer}>
                    <FaSearch style={{ position: 'absolute', left: '18px', top: '18px', color: 'var(--text-secondary)' }} />
                    <input
                        type="text"
                        placeholder="Filtrar por referencia o cliente corporativo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                <div className={styles.legendContainer}>
                    {[
                        { label: "Sin iniciar", color: styles.creado },
                        { label: "En curso", color: styles.enProceso },
                        { label: "En pausa", color: styles.enPausa },
                        { label: "Cancelado", color: styles.cancelado },
                        { label: "Finalizado", color: styles.finalizado },
                        { label: "Mostrar todos", color: styles.todos, value: null }
                    ].map((item, idx) => (
                        <div
                            key={idx}
                            className={`${styles.legendItem} ${estadoFiltro === (item.value !== undefined ? item.value : item.label) ? styles.legendItemActive : ""}`}
                            onClick={() => setEstadoFiltro(item.value !== undefined ? item.value : item.label)}
                        >
                            <span className={item.color}></span> {item.label}
                        </div>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className={styles.spinnerContainer}>
                    <HashLoader color="#00c6ff" loading={loading} size={60} />
                    <p>Sincronizando con la base de datos operativa...</p>
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th><FaFileAlt style={{ marginRight: '8px' }} /> Referencia</th>
                                <th><FaCalendarAlt style={{ marginRight: '8px' }} /> Última Actividad</th>
                                <th><FaBuilding style={{ marginRight: '8px' }} /> Cliente</th>
                                <th><FaMapMarkerAlt style={{ marginRight: '8px' }} /> Ubicación / Lugar</th>
                                <th><FaIndustry style={{ marginRight: '8px' }} /> Material</th>
                                <th><FaWeightHanging style={{ marginRight: '8px' }} /> Acumulado (t)</th>
                                <th><FaBox style={{ marginRight: '8px' }} /> Lote</th>
                                <th><FaCheckCircle style={{ marginRight: '8px' }} /> Estado</th>
                                <th style={{ width: '40px' }}></th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredRecords.length > 0 ? (
                                filteredRecords.map((refData) => {
                                    const estadoNormal = refData.descripcion_estado === "Lote cerrado"
                                        ? "En curso"
                                        : refData.descripcion_estado === "Creado"
                                            ? "Sin iniciar"
                                            : refData.descripcion_estado;

                                    const badgeClass = `badge-${estadoNormal.toLowerCase().replace(/\s+/g, '-')}`;

                                    return (
                                        <tr
                                            key={refData.Idref}
                                            className={styles.row}
                                            onClick={() => handleRowClick(refData)}
                                        >
                                            <td style={{ fontWeight: '700', color: 'white' }}>{refData.ref}</td>
                                            <td style={{ color: 'var(--text-secondary)' }}>{formatDate(refData.fecha_mayor)}</td>
                                            <td>{refData.nombre_cliente}</td>
                                            <td>
                                                <div style={{ fontSize: '0.8rem' }}>{refData.nombre_ubicacion}</div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{refData.nombre_lugar}</div>
                                            </td>
                                            <td>{refData.nombre_material}</td>
                                            <td style={{ fontFamily: 'var(--font-title)', fontWeight: '700', color: 'var(--secondary)' }}>
                                                {formatAcomulado(refData.acomulado)}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span style={{ background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--glass-border)', whiteSpace: 'nowrap' }}>
                                                    {refData.lote_en_progreso}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${styles[badgeClass]}`}>
                                                    {estadoNormal}
                                                </span>
                                            </td>
                                            <td>
                                                <FaArrowRight style={{ color: 'var(--glass-border)', fontSize: '0.8rem' }} />
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
                                        No se encontraron registros activos para el filtro seleccionado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Inbound;
